import { useState, useMemo, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSelect from "components/SoftSelect";

import { parseMissionImageRef, getMissionDocumentSignedUrl } from "utils/missionDocuments";

import {
  parseRouteLocationsFromDescription,
  stripRouteBlockFromDescription,
} from "../../../resourceRequestLocations";
import {
  getResourceRequestQuestions,
  emptyPassengerQuestionFields,
  groupQuestionsBySection,
  isStemFlightType,
} from "./resourceRequestQuestionBank";

const STEP_KEYS = {
  BASIC: "basic",
  LOCATIONS: "locations",
  REQUIREMENTS: "requirements",
  CARGO: "cargo",
  PASSENGERS: "passengers",
};

const defaultCargoItem = () => ({
  description: "",
  quantity: "",
  weightLbs: "",
});

/** Split stored description + optional [Route] block (see graphqlService saveResourceRequest). */
function parseInitialResourceRequest(data) {
  if (!data || typeof data !== "object") {
    return {
      description: "",
      locationCity: "",
      locationState: "",
      arrivalCity: "",
      arrivalState: "",
    };
  }
  const rawDesc = data.description != null ? String(data.description) : "";
  let locationCity = data.locationCity || "";
  let locationState =
    data.locationState && !["Yes", "No"].includes(data.locationState)
      ? data.locationState
      : "";
  let arrivalCity = data.arrivalCity || "";
  let arrivalState =
    data.arrivalState && !["Yes", "No"].includes(data.arrivalState)
      ? data.arrivalState
      : "";

  const fromRoute = parseRouteLocationsFromDescription(rawDesc);
  if (!locationCity && !locationState) {
    locationCity = fromRoute.locationCity;
    locationState = fromRoute.locationState;
  }
  if (!arrivalCity && !arrivalState) {
    arrivalCity = fromRoute.arrivalCity;
    arrivalState = fromRoute.arrivalState;
  }

  const description = stripRouteBlockFromDescription(rawDesc);

  return {
    description,
    locationCity,
    locationState,
    arrivalCity,
    arrivalState,
  };
}

const defaultPassengerItem = () => ({
  fullName: "",
  age: "",
  weightLbs: "",
  specialNeeds: "",
  isAnimal: "No",
  ...emptyPassengerQuestionFields(),
});

const normalizeIsAnimal = (value) =>
  value === true || String(value || "").trim().toLowerCase() === "yes"
    ? "Yes"
    : "No";

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
].map((s) => ({ value: s, label: s }));

const YES_NO_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

const TYPE_OPTIONS = [
  { value: "Medical", label: "Medical" },
  { value: "Animal Rescue", label: "Animal Rescue" },
  { value: "Disaster Relief", label: "Disaster Relief" },
  { value: "Veteran Support", label: "Veteran Support" },
  { value: "Conservation", label: "Conservation" },
  { value: "Aviation Experience", label: "STEM Flight" },
  { value: "other", label: "Others" },
];

const IMAGE_ACCEPT = "image/jpeg,image/jpg,image/png,image/gif,image/webp";

function ImageThumbnail({ file, onRemove }) {
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const fileName =
    file?.name?.length > 22 ? `${file.name.slice(0, 19)}…` : file?.name;

  return (
    <SoftBox
      sx={{
        width: 160,
        flexShrink: 0,
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 4px 14px rgba(15, 34, 58, 0.12)",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <SoftBox
        position="relative"
        sx={{
          width: "100%",
          height: 120,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.04)",
        }}
      >
        {previewUrl ? (
          <SoftBox
            component="img"
            src={previewUrl}
            alt={file?.name}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
            }}
          />
        ) : (
          <Icon sx={{ fontSize: 48, color: "text.secondary" }}>image</Icon>
        )}
        <IconButton
          size="small"
          onClick={(e) => {
            e.preventDefault();
            onRemove();
          }}
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            width: 28,
            height: 28,
          }}
        >
          <Icon sx={{ fontSize: 18 }}>close</Icon>
        </IconButton>
      </SoftBox>
      <SoftBox
        px={1}
        py={1}
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.03)",
        }}
      >
        <SoftTypography
          variant="caption"
          color="text"
          display="block"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={file?.name}
        >
          {fileName}
        </SoftTypography>
      </SoftBox>
    </SoftBox>
  );
}

function SavedImagePreview({ imageRef, idToken, onRemove }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const label =
    imageRef?.originalName || imageRef?.fileName || "Saved image";
  const displayName =
    label.length > 22 ? `${label.slice(0, 19)}…` : label;

  useEffect(() => {
    if (!imageRef?.fileName || !idToken) {
      setPreviewUrl(null);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getMissionDocumentSignedUrl(
      {
        fileName: imageRef.fileName,
        bucket: imageRef.bucket,
        key: imageRef.fileName,
      },
      idToken,
    )
      .then((url) => {
        if (!cancelled) setPreviewUrl(url);
      })
      .catch((err) => {
        if (!cancelled) {
          setPreviewUrl(null);
          setError(err?.message || "Could not load image");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [imageRef?.fileName, imageRef?.bucket, idToken]);

  return (
    <SoftBox
      sx={{
        width: 160,
        flexShrink: 0,
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 4px 14px rgba(15, 34, 58, 0.12)",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <SoftBox
        position="relative"
        sx={{
          width: "100%",
          height: 120,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.04)",
        }}
      >
        {loading ? (
          <CircularProgress size={28} />
        ) : previewUrl ? (
          <SoftBox
            component="img"
            src={previewUrl}
            alt={label}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
            }}
          />
        ) : (
          <Icon sx={{ fontSize: 48, color: "text.secondary" }}>broken_image</Icon>
        )}
        {onRemove ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              onRemove();
            }}
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              width: 28,
              height: 28,
            }}
          >
            <Icon sx={{ fontSize: 18 }}>close</Icon>
          </IconButton>
        ) : null}
      </SoftBox>
      <SoftBox
        px={1}
        py={1}
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.03)",
        }}
      >
        <SoftTypography
          variant="caption"
          color="text"
          display="block"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={label}
        >
          {displayName}
        </SoftTypography>
        {error ? (
          <SoftTypography variant="caption" color="error" display="block">
            {error}
          </SoftTypography>
        ) : null}
      </SoftBox>
    </SoftBox>
  );
}

function ResourceRequestForm({
  onSubmit,
  onCancel,
  initialData = null,
  loading = false,
  idToken = null,
}) {
  const initialType = initialData?.type || "";
  const isInitialDraft =
    String(initialData?.status || "").trim().toLowerCase() === "draft";
  const isTypeOther =
    initialType && !TYPE_OPTIONS.find((o) => o.value === initialType);
  const parsedLoc = parseInitialResourceRequest(initialData);
  const passengerSource =
    initialData?.PassengerItem?.length
      ? initialData.PassengerItem
      : initialData?.passengerItem?.length
        ? initialData.passengerItem
        : [];
  const initialStemPassenger = passengerSource[0] || {};
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    title: initialData?.title || "",
    description: parsedLoc.description,
    additionalNotes: initialData?.additionalNotes || "",
    type: isTypeOther ? "other" : initialType,
    customType: isTypeOther ? initialType : "",
    studentName: initialStemPassenger.fullName || "",
    studentCity: initialStemPassenger.studentLocation || "",
    dueDate: initialData?.dueDate || "",
    requesterName: initialData?.requesterName || "",
    locationCity: parsedLoc.locationCity,
    locationState: parsedLoc.locationState,
    arrivalCity: parsedLoc.arrivalCity,
    arrivalState: parsedLoc.arrivalState,
    includesCargo:
      initialData?.includesCargo ||
      (initialData?.cargoItems?.length ? "Yes" : "") ||
      "",
    includesPassengers:
      initialData?.includesPassengers ||
      (passengerSource.length ? "Yes" : "") ||
      "",
    cargoItems: initialData?.cargoItems?.length
      ? initialData.cargoItems.map((c) => ({ ...defaultCargoItem(), ...c }))
      : [defaultCargoItem()],
    PassengerItem: passengerSource.length
      ? passengerSource.map((p) => ({
          ...defaultPassengerItem(),
          ...p,
          isAnimal: normalizeIsAnimal(p.isAnimal),
        }))
      : [defaultPassengerItem()],
    isAsap:
      initialData?.isAsap === true ||
      (typeof initialData?.isAsap === "string" &&
        initialData.isAsap.toLowerCase() === "yes"),
    image: null,
  });
  const existingImage = useMemo(
    () => parseMissionImageRef(initialData?.image),
    [initialData?.image],
  );
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null);
  const [existingImageRemoved, setExistingImageRemoved] = useState(false);
  const isBusy = loading || Boolean(uploadProgress);
  const showExistingImage =
    existingImage?.fileName && !existingImageRemoved && !formData.image;
  const requestType =
    formData.type === "other" ? formData.customType : formData.type;
  const isAnimalRescueType = requestType === "Animal Rescue";
  const isStemFlightRequest = isStemFlightType(requestType);

  const steps = useMemo(() => {
    const list = ["Basic Info", "Locations"];
    if (formData.includesCargo === "Yes") list.push("Cargo Items");
    if (formData.includesPassengers === "Yes") list.push("Passengers");
    return list;
  }, [formData.includesCargo, formData.includesPassengers]);

  const currentStepKey = useMemo(() => {
    let i = 0;
    if (activeStep === i++) return STEP_KEYS.BASIC;
    if (activeStep === i++) return STEP_KEYS.LOCATIONS;
    if (formData.includesCargo === "Yes" && activeStep === i++)
      return STEP_KEYS.CARGO;
    if (formData.includesPassengers === "Yes" && activeStep === i++)
      return STEP_KEYS.PASSENGERS;
    return formData.includesPassengers === "Yes"
      ? STEP_KEYS.PASSENGERS
      : formData.includesCargo === "Yes"
      ? STEP_KEYS.CARGO
      : STEP_KEYS.LOCATIONS;
  }, [activeStep, formData.includesCargo, formData.includesPassengers]);

  useEffect(() => {
    if (activeStep >= steps.length) {
      setActiveStep(Math.max(0, steps.length - 1));
    }
  }, [steps.length, activeStep]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (isStemFlightType(prev.type === "other" ? prev.customType : prev.type)) {
        if (field === "studentName") {
          const passengers = [...prev.PassengerItem];
          const row = { ...(passengers[0] || defaultPassengerItem()) };
          row.fullName = String(value || "");
          passengers[0] = row;
          next.PassengerItem = passengers;
        }
        if (field === "studentCity") {
          const passengers = [...prev.PassengerItem];
          const row = { ...(passengers[0] || defaultPassengerItem()) };
          row.studentLocation = String(value || "");
          passengers[0] = row;
          next.PassengerItem = passengers;
        }
      }
      return next;
    });
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleCargoChange = (index, field, value) => {
    const next = [...formData.cargoItems];
    next[index] = { ...next[index], [field]: value };
    setFormData((prev) => ({ ...prev, cargoItems: next }));
  };

  const handlePassengerChange = (index, field, value) => {
    const next = [...formData.PassengerItem];
    next[index] = { ...next[index], [field]: value };
    setFormData((prev) => ({ ...prev, PassengerItem: next }));
    const errorKey = `passenger_${index}_${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => ({ ...prev, [errorKey]: "" }));
    }
  };

  const addCargoItem = () => {
    setFormData((prev) => ({
      ...prev,
      cargoItems: [...prev.cargoItems, defaultCargoItem()],
    }));
  };

  const removeCargoItem = (index) => {
    if (formData.cargoItems.length <= 1) return;
    setFormData((prev) => ({
      ...prev,
      cargoItems: prev.cargoItems.filter((_, i) => i !== index),
    }));
  };

  const addPassengerItem = () => {
    setFormData((prev) => ({
      ...prev,
      PassengerItem: [...prev.PassengerItem, defaultPassengerItem()],
    }));
  };

  const removePassengerItem = (index) => {
    if (formData.PassengerItem.length <= 1) return;
    setFormData((prev) => ({
      ...prev,
      PassengerItem: prev.PassengerItem.filter((_, i) => i !== index),
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !file.type?.startsWith("image/")) return;
    setFormData((prev) => ({ ...prev, image: file }));
    if (submitError) setSubmitError("");
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, image: null }));
  };

  const removeExistingImage = () => {
    setExistingImageRemoved(true);
  };

  const buildPayload = () => {
    const payload = { ...formData };
    if (formData.type === "other" && formData.customType?.trim()) {
      payload.type = formData.customType.trim();
    }
    payload.isAsap = formData.isAsap ? "Yes" : "No";
    const hasCargo = formData.includesCargo === "Yes";
    const hasPassengers = formData.includesPassengers === "Yes";
    payload.cargoItems = hasCargo
      ? formData.cargoItems
          ?.filter((c) => c.description || c.weightLbs || c.quantity)
          .map((c) => ({
            description: c.description || null,
            quantity:
              c.quantity != null && String(c.quantity).trim() !== ""
                ? String(c.quantity).trim()
                : null,
            weightLbs: c.weightLbs ? parseInt(String(c.weightLbs), 10) : null,
          })) ?? []
      : [];
    const passengerRows = hasPassengers
      ? formData.PassengerItem?.filter(
          (p) =>
            p.fullName ||
            p.age ||
            p.weightLbs ||
            p.specialNeeds ||
            normalizeIsAnimal(p.isAnimal) === "Yes",
        ).map((p) => {
          const isAnimal = isAnimalRescueType
            ? normalizeIsAnimal(p.isAnimal)
            : "No";
          const row = {
            fullName: p.fullName || null,
            age: p.age ? parseInt(String(p.age), 10) : null,
            weightLbs: p.weightLbs ? parseInt(String(p.weightLbs), 10) : null,
            specialNeeds: p.specialNeeds || null,
            isAnimal,
          };
          const questionsForRow = getResourceRequestQuestions(requestType, {
            isAnimal: isAnimal === "Yes",
          });
          questionsForRow.forEach((q) => {
            const value = p[q.id];
            row[q.id] =
              value != null && String(value).trim() !== ""
                ? String(value).trim()
                : null;
          });
          return row;
        }) ?? []
      : [];

    if (isStemFlightRequest) {
      const studentName = String(formData.studentName || "").trim();
      const studentCity = String(formData.studentCity || "").trim();
      if (studentName || studentCity) {
        if (passengerRows.length === 0) {
          passengerRows.push({
            fullName: studentName || null,
            studentLocation: studentCity || null,
            isAnimal: "No",
          });
        } else {
          passengerRows[0] = {
            ...passengerRows[0],
            fullName: passengerRows[0].fullName || studentName || null,
            studentLocation:
              passengerRows[0].studentLocation || studentCity || null,
          };
        }
      }
    }

    payload.passengerItem = passengerRows;
    payload.PassengerItem = passengerRows;
    if (!formData.image && existingImage?.fileName && !existingImageRemoved) {
      payload.image = {
        fileName: existingImage.fileName,
        bucket: existingImage.bucket || "info.avipair.com",
      };
    }
    return payload;
  };

  const submitRequest = async (statusOverride = null) => {
    const isDraft = statusOverride === "Draft";
    if (!isDraft && !validateStep(activeStep)) return;
    setSubmitError("");
    setUploadProgress(null);
    try {
      const payload = buildPayload();
      if (statusOverride) payload.status = statusOverride;
      const result = onSubmit(payload, {
        pendingImageFiles: formData.image ? [formData.image] : [],
        onUploadProgress: (name, percent) =>
          setUploadProgress({ name, percent }),
      });
      if (result && typeof result.then === "function") {
        await result;
      }
    } catch (err) {
      setSubmitError(err.message || "Failed to save resource request");
    } finally {
      setUploadProgress(null);
    }
  };

  const handleSubmit = () => {
    if (!initialData || isInitialDraft) {
      submitRequest("Active");
      return;
    }
    submitRequest();
  };

  const handleSaveAsDraft = () => {
    submitRequest("Draft");
  };

  const validateStep = (step) => {
    const next = {};
    if (step === 0) {
      if (!formData.title?.trim()) next.title = "Title is required";
      if (formData.type === "other" && !formData.customType?.trim()) {
        next.customType = "Please specify the type";
      }
      if (isStemFlightRequest) {
        if (!formData.studentName?.trim()) {
          next.studentName = "Student name is required for STEM Flight requests";
        }
        if (!formData.studentCity?.trim()) {
          next.studentCity = "Student city is required for STEM Flight requests";
        }
      }
    }
    if (currentStepKey === STEP_KEYS.PASSENGERS) {
      formData.PassengerItem.forEach((passenger, index) => {
        const questionsForRow = getResourceRequestQuestions(requestType, {
          isAnimal: normalizeIsAnimal(passenger.isAnimal) === "Yes",
        });
        questionsForRow
          .filter((item) => item.required)
          .forEach((item) => {
            const response = passenger[item.id];
            if (response == null || String(response).trim() === "") {
              next[`passenger_${index}_${item.id}`] =
                `${item.label} is required`;
            }
          });
      });
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) return;
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const isLastStep = activeStep === steps.length - 1;

  const renderStepContent = () => {
    switch (currentStepKey) {
      case STEP_KEYS.BASIC:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <SoftBox mb={1}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Title *
                </SoftTypography>
              </SoftBox>
              <SoftInput
                placeholder="Request title"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                error={!!errors.title}
              />
              {errors.title && (
                <SoftTypography variant="caption" color="error">
                  {errors.title}
                </SoftTypography>
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              <SoftBox mb={1}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Type
                </SoftTypography>
              </SoftBox>
              <SoftSelect
                placeholder="Type"
                options={TYPE_OPTIONS}
                value={TYPE_OPTIONS.find((o) => o.value === formData.type)}
                onChange={(opt) => {
                  const newType = opt?.value ?? "";
                  setFormData((prev) => {
                    const next = { ...prev, type: newType };
                    if (newType !== "other") next.customType = "";
                    if (!isStemFlightType(newType === "other" ? next.customType : newType)) {
                      next.studentName = "";
                      next.studentCity = "";
                    }
                    return next;
                  });
                  if (newType !== "other" && errors.customType) {
                    setErrors((prev) => ({ ...prev, customType: "" }));
                  }
                  if (!isStemFlightType(newType)) {
                    setErrors((prev) => ({
                      ...prev,
                      studentName: "",
                      studentCity: "",
                    }));
                  }
                }}
              />
            </Grid>
            {formData.type === "other" && (
              <Grid item xs={12} md={3}>
                <SoftBox mb={1}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Specify type
                  </SoftTypography>
                </SoftBox>
                <SoftInput
                  placeholder="Enter custom type"
                  value={formData.customType}
                  onChange={(e) => handleChange("customType", e.target.value)}
                  error={!!errors.customType}
                />
                {errors.customType && (
                  <SoftTypography
                    variant="caption"
                    color="error"
                    display="block"
                  >
                    {errors.customType}
                  </SoftTypography>
                )}
              </Grid>
            )}
            {isStemFlightRequest ? (
              <>
                <Grid item xs={12} md={6}>
                  <SoftBox mb={1}>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Student name *
                    </SoftTypography>
                  </SoftBox>
                  <SoftInput
                    placeholder="Student full name"
                    value={formData.studentName}
                    onChange={(e) => handleChange("studentName", e.target.value)}
                    error={!!errors.studentName}
                  />
                  {errors.studentName ? (
                    <SoftTypography variant="caption" color="error">
                      {errors.studentName}
                    </SoftTypography>
                  ) : null}
                </Grid>
                <Grid item xs={12} md={6}>
                  <SoftBox mb={1}>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Student city *
                    </SoftTypography>
                  </SoftBox>
                  <SoftInput
                    placeholder="Student city"
                    value={formData.studentCity}
                    onChange={(e) => handleChange("studentCity", e.target.value)}
                    error={!!errors.studentCity}
                  />
                  {errors.studentCity ? (
                    <SoftTypography variant="caption" color="error">
                      {errors.studentCity}
                    </SoftTypography>
                  ) : null}
                </Grid>
              </>
            ) : null}
            <Grid item xs={12} md={6}>
              <SoftBox mb={1}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Due date
                </SoftTypography>
              </SoftBox>
              <SoftInput
                type="date"
                value={formData.dueDate}
                onChange={(e) => handleChange("dueDate", e.target.value)}
              />
              <FormControlLabel
                style={{ marginLeft: 2 }}
                control={
                  <Checkbox
                    checked={!!formData.isAsap}
                    onChange={(e) => handleChange("isAsap", e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <SoftTypography variant="caption">Is Urgent</SoftTypography>
                }
              />
            </Grid>
            {/* Spacer keeps "Includes cargo / passengers" on the row below (same as when expiration sat here). */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: { xs: "none", md: "block" } }}
              aria-hidden
            />

            <Grid item xs={12} md={4}>
              <SoftBox mb={1}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Includes cargo
                </SoftTypography>
              </SoftBox>
              <SoftSelect
                placeholder="Yes / No"
                options={YES_NO_OPTIONS}
                value={YES_NO_OPTIONS.find(
                  (o) => o.value === formData.includesCargo,
                )}
                onChange={(opt) =>
                  handleChange("includesCargo", opt?.value ?? "")
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SoftBox mb={1}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Includes passengers
                </SoftTypography>
              </SoftBox>
              <SoftSelect
                placeholder="Yes / No"
                options={YES_NO_OPTIONS}
                value={YES_NO_OPTIONS.find(
                  (o) => o.value === formData.includesPassengers,
                )}
                onChange={(opt) =>
                  handleChange("includesPassengers", opt?.value ?? "")
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SoftBox mb={1}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Requester name
                </SoftTypography>
              </SoftBox>
              <SoftInput
                placeholder="Requester name"
                value={formData.requesterName}
                onChange={(e) => handleChange("requesterName", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <SoftBox mb={1}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Description
                </SoftTypography>
              </SoftBox>
              <SoftInput
                multiline
                rows={2}
                placeholder="Description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <SoftBox mb={1}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Additional Notes
                </SoftTypography>
              </SoftBox>
              <SoftInput
                multiline
                rows={3}
                placeholder="Share any additional details not covered above"
                value={formData.additionalNotes}
                onChange={(e) =>
                  handleChange("additionalNotes", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <SoftBox mb={1}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Image
                </SoftTypography>
              </SoftBox>
              <input
                type="file"
                accept={IMAGE_ACCEPT}
                onChange={handleImageChange}
                style={{ display: "none" }}
                id="resource-request-image"
              />
              <label htmlFor="resource-request-image">
                <SoftButton
                  variant="outlined"
                  color="secondary"
                  component="span"
                  startIcon={<Icon>add_photo_alternate</Icon>}
                  disabled={isBusy}
                >
                  {formData.image || showExistingImage ? "Replace image" : "Upload image"}
                </SoftButton>
              </label>
              <SoftTypography
                variant="caption"
                color="text"
                display="block"
                mt={0.5}
              >
                One image per request (JPEG, PNG, GIF, WebP).
              </SoftTypography>
              {uploadProgress ? (
                <SoftTypography variant="caption" color="info" display="block" mt={1}>
                  Uploading {uploadProgress.name}… {uploadProgress.percent}%
                </SoftTypography>
              ) : null}
              {submitError ? (
                <SoftTypography variant="caption" color="error" display="block" mt={0.5}>
                  {submitError}
                </SoftTypography>
              ) : null}
              {formData.image ? (
                <SoftBox display="flex" flexWrap="wrap" gap={2} mt={2}>
                  <ImageThumbnail file={formData.image} onRemove={removeImage} />
                </SoftBox>
              ) : showExistingImage ? (
                <SoftBox display="flex" flexWrap="wrap" gap={2} mt={2}>
                  <SavedImagePreview
                    imageRef={existingImage}
                    idToken={idToken}
                    onRemove={removeExistingImage}
                  />
                </SoftBox>
              ) : null}
            </Grid>
          </Grid>
        );

      case STEP_KEYS.LOCATIONS:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SoftTypography
                variant="button"
                fontWeight="bold"
                color="secondary"
                display="block"
                mb={1}
              >
                Origin
              </SoftTypography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SoftBox mb={1}>
                <SoftTypography variant="caption" fontWeight="bold">
                  City
                </SoftTypography>
              </SoftBox>
              <SoftInput
                placeholder="City"
                value={formData.locationCity}
                onChange={(e) => handleChange("locationCity", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SoftBox mb={1}>
                <SoftTypography variant="caption" fontWeight="bold">
                  State
                </SoftTypography>
              </SoftBox>
              <SoftSelect
                placeholder="State"
                options={US_STATES}
                value={
                  formData.locationState &&
                  US_STATES.some((o) => o.value === formData.locationState)
                    ? US_STATES.find((o) => o.value === formData.locationState)
                    : null
                }
                onChange={(opt) =>
                  handleChange("locationState", opt?.value ?? "")
                }
              />
            </Grid>
            <Grid item xs={12}>
              <SoftTypography
                variant="button"
                fontWeight="bold"
                color="secondary"
                display="block"
                mt={2}
                mb={1}
              >
                Destination
              </SoftTypography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SoftBox mb={1}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Arrival city
                </SoftTypography>
              </SoftBox>
              <SoftInput
                placeholder="Arrival city"
                value={formData.arrivalCity}
                onChange={(e) => handleChange("arrivalCity", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SoftBox mb={1}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Arrival state
                </SoftTypography>
              </SoftBox>
              <SoftSelect
                placeholder="State"
                options={US_STATES}
                value={
                  formData.arrivalState &&
                  US_STATES.some((o) => o.value === formData.arrivalState)
                    ? US_STATES.find((o) => o.value === formData.arrivalState)
                    : null
                }
                onChange={(opt) =>
                  handleChange("arrivalState", opt?.value ?? "")
                }
              />
            </Grid>
          </Grid>
        );

      case STEP_KEYS.CARGO:
        return (
          <SoftBox>
            {formData.cargoItems.map((item, index) => (
              <SoftBox
                key={index}
                mb={2}
                p={1.5}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} md={4}>
                    <SoftTypography variant="caption">
                      Description
                    </SoftTypography>
                    <SoftInput
                      size="small"
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) =>
                        handleCargoChange(index, "description", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <SoftTypography variant="caption">Qty</SoftTypography>
                    <SoftInput
                      size="small"
                      placeholder="e.g. 5"
                      value={item.quantity}
                      onChange={(e) =>
                        handleCargoChange(index, "quantity", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <SoftTypography variant="caption">
                      Weight (lbs)
                    </SoftTypography>
                    <SoftInput
                      type="number"
                      size="small"
                      placeholder="lbs"
                      value={item.weightLbs}
                      onChange={(e) =>
                        handleCargoChange(index, "weightLbs", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={2}
                    display="flex"
                    alignItems="flex-end"
                  >
                    <SoftButton
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => removeCargoItem(index)}
                      disabled={formData.cargoItems.length <= 1}
                    >
                      Remove
                    </SoftButton>
                  </Grid>
                </Grid>
              </SoftBox>
            ))}
            <SoftButton
              size="small"
              variant="outlined"
              color="info"
              onClick={addCargoItem}
              startIcon={<Icon>add</Icon>}
            >
              Add Cargo Item
            </SoftButton>
          </SoftBox>
        );

      case STEP_KEYS.PASSENGERS:
        return (
          <SoftBox>
            {isAnimalRescueType ? (
              <SoftBox mb={2}>
                <SoftTypography variant="caption" color="text">
                  Add animals here as passengers, not cargo. Use Is Animal on each
                  row to mark whether the passenger is an animal.
                </SoftTypography>
              </SoftBox>
            ) : null}
            {formData.PassengerItem.map((item, index) => (
              <SoftBox
                key={index}
                mb={2}
                p={1.5}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} md={isAnimalRescueType ? 3 : 4}>
                    <SoftTypography variant="caption">
                      {isAnimalRescueType
                        ? "Full Name / Animal Name"
                        : "Full Name"}
                    </SoftTypography>
                    <SoftInput
                      size="small"
                      placeholder={
                        isAnimalRescueType
                          ? "Passenger or animal name"
                          : "Passenger name"
                      }
                      value={item.fullName}
                      onChange={(e) =>
                        handlePassengerChange(index, "fullName", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6} md={1}>
                    <SoftTypography variant="caption">Age</SoftTypography>
                    <SoftInput
                      type="number"
                      size="small"
                      placeholder="Age"
                      value={item.age}
                      onChange={(e) =>
                        handlePassengerChange(index, "age", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <SoftTypography variant="caption">
                      Weight (lbs)
                    </SoftTypography>
                    <SoftInput
                      type="number"
                      size="small"
                      placeholder="lbs"
                      value={item.weightLbs}
                      onChange={(e) =>
                        handlePassengerChange(
                          index,
                          "weightLbs",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={isAnimalRescueType ? 3 : 4}>
                    <SoftTypography variant="caption">
                      Special Needs
                    </SoftTypography>
                    <SoftInput
                      size="small"
                      placeholder="Special needs"
                      value={item.specialNeeds}
                      onChange={(e) =>
                        handlePassengerChange(
                          index,
                          "specialNeeds",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                  {isAnimalRescueType ? (
                    <Grid item xs={12} md={2}>
                      <SoftTypography variant="caption">
                        Is Animal
                      </SoftTypography>
                      <SoftSelect
                        size="small"
                        options={YES_NO_OPTIONS}
                        value={YES_NO_OPTIONS.find(
                          (option) =>
                            option.value === normalizeIsAnimal(item.isAnimal),
                        )}
                        onChange={(option) =>
                          handlePassengerChange(
                            index,
                            "isAnimal",
                            option?.value || "No",
                          )
                        }
                      />
                    </Grid>
                  ) : null}
                  <Grid
                    item
                    xs={12}
                    md={1}
                    display="flex"
                    alignItems="flex-end"
                  >
                    <SoftButton
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => removePassengerItem(index)}
                      disabled={formData.PassengerItem.length <= 1}
                    >
                      Remove
                    </SoftButton>
                  </Grid>
                </Grid>

                {(() => {
                  const passengerQuestions = getResourceRequestQuestions(
                    requestType,
                    {
                      isAnimal: normalizeIsAnimal(item.isAnimal) === "Yes",
                    },
                  );
                  if (!passengerQuestions.length) return null;
                  const sections = groupQuestionsBySection(passengerQuestions);

                  return (
                    <SoftBox
                      mt={2}
                      pt={2}
                      sx={{ borderTop: "1px solid", borderColor: "divider" }}
                    >
                      <SoftTypography
                        variant="caption"
                        color="text"
                        display="block"
                        mb={1.5}
                      >
                        Details for this passenger ({requestType})
                      </SoftTypography>
                      {Object.entries(sections).map(([section, questions]) => (
                        <SoftBox key={section} mb={2}>
                          <SoftTypography
                            variant="button"
                            fontWeight="bold"
                            color="secondary"
                            display="block"
                            mb={1}
                          >
                            {section}
                          </SoftTypography>
                          <Grid container spacing={2}>
                            {questions.map((questionItem) => {
                              const errorKey = `passenger_${index}_${questionItem.id}`;
                              const value = item[questionItem.id] ?? "";
                              const isLongText =
                                questionItem.type === "textarea";

                              return (
                                <Grid
                                  item
                                  xs={12}
                                  md={isLongText ? 12 : 6}
                                  key={questionItem.id}
                                >
                                  <SoftBox mb={0.5}>
                                    <SoftTypography
                                      variant="caption"
                                      fontWeight="bold"
                                    >
                                      {questionItem.label}
                                      {questionItem.required ? " *" : ""}
                                    </SoftTypography>
                                  </SoftBox>
                                  {questionItem.type === "select" ? (
                                    <SoftSelect
                                      placeholder="Select"
                                      options={(questionItem.options || []).map(
                                        (option) => ({
                                          value: option,
                                          label: option,
                                        }),
                                      )}
                                      value={
                                        value ? { value, label: value } : null
                                      }
                                      onChange={(option) =>
                                        handlePassengerChange(
                                          index,
                                          questionItem.id,
                                          option?.value || "",
                                        )
                                      }
                                      error={!!errors[errorKey]}
                                    />
                                  ) : (
                                    <SoftInput
                                      type={
                                        [
                                          "email",
                                          "tel",
                                          "number",
                                          "date",
                                        ].includes(questionItem.type)
                                          ? questionItem.type
                                          : "text"
                                      }
                                      multiline={isLongText}
                                      rows={isLongText ? 3 : undefined}
                                      value={value}
                                      onChange={(event) =>
                                        handlePassengerChange(
                                          index,
                                          questionItem.id,
                                          event.target.value,
                                        )
                                      }
                                      error={!!errors[errorKey]}
                                    />
                                  )}
                                  {errors[errorKey] && (
                                    <SoftTypography
                                      variant="caption"
                                      color="error"
                                    >
                                      {errors[errorKey]}
                                    </SoftTypography>
                                  )}
                                </Grid>
                              );
                            })}
                          </Grid>
                        </SoftBox>
                      ))}
                    </SoftBox>
                  );
                })()}
              </SoftBox>
            ))}
            <SoftButton
              size="small"
              variant="outlined"
              color="info"
              onClick={addPassengerItem}
              startIcon={<Icon>add</Icon>}
            >
              Add Passenger
            </SoftButton>
          </SoftBox>
        );

      default:
        return null;
    }
  };

  return (
    <SoftBox>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <SoftBox minHeight={280}>{renderStepContent()}</SoftBox>

      <SoftBox mt={2} display="flex" justifyContent="space-between">
        <SoftBox display="flex" gap={2}>
          <SoftButton
            variant="outlined"
            color="secondary"
            onClick={onCancel}
            disabled={isBusy}
          >
            Cancel
          </SoftButton>
          {activeStep > 0 && (
            <SoftButton
              variant="outlined"
              color="dark"
              onClick={handleBack}
              disabled={isBusy}
              startIcon={<Icon>arrow_back</Icon>}
            >
              Back
            </SoftButton>
          )}
        </SoftBox>
        <SoftBox display="flex" gap={2} alignItems="center">
          {(!initialData || isInitialDraft) && (
            <SoftButton
              variant="outlined"
              color="secondary"
              onClick={handleSaveAsDraft}
              disabled={isBusy}
              startIcon={
                isBusy ? (
                  <CircularProgress size={16} sx={{ color: "inherit" }} />
                ) : (
                  <Icon>save</Icon>
                )
              }
            >
              {isBusy ? "Saving..." : "Save Draft"}
            </SoftButton>
          )}
          {isLastStep ? (
            <SoftButton
              variant="gradient"
              color="info"
              onClick={handleSubmit}
              disabled={isBusy}
            >
              {isBusy ? (
                <>
                  <CircularProgress
                    size={16}
                    sx={{ mr: 1, color: "white" }}
                  />
                  Saving...
                </>
              ) : (
                <>
                  <Icon sx={{ mr: 1 }}>
                    {isInitialDraft ? "publish" : "save"}
                  </Icon>
                  {!initialData
                    ? "Create Request"
                    : isInitialDraft
                      ? "Publish Request"
                      : "Save Changes"}
                </>
              )}
            </SoftButton>
          ) : (
            <SoftButton
              variant="gradient"
              color="info"
              onClick={handleNext}
              endIcon={<Icon>arrow_forward</Icon>}
            >
              Next
            </SoftButton>
          )}
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

export default ResourceRequestForm;
